import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiExercise } from '@codey2/codey-api-types/models';
import ContentContainer from '../../components/common/ContentContainer/ContentContainer';
import styles from './ExerciseAttemptPage.module.css';
import { Header } from '../../components/common/Header';
import { StepViewer } from './components/StepViewer';
import { ReadOnlyAssetsTab } from './components/ReadOnlyAssetsTab';
import { useExercisesApi } from '../../app/api/useExercisesApi';
import { useSnackbar } from '../../navigation/snackbar';
import { BackButton } from '../../components/common/BackButton/BackButton';
import Button from '../../components/common/Button/Button';

export const ExerciseAttemptPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getExerciseById } = useExercisesApi();
  const { showSnackbar } = useSnackbar();

  const [exercise, setExercise] = useState<ApiExercise | undefined>();
  const [isLoadingExercise, setIsLoadingExercise] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  useEffect(() => {
    if (id) {
      const loadExercise = async () => {
        setIsLoadingExercise(true);
        try {
          const fetchedExercise = await getExerciseById(Number(id));
          setExercise(fetchedExercise);
        } catch {
          showSnackbar('Opdracht kon niet worden geladen', 'error');
        } finally {
          setIsLoadingExercise(false);
        }
      };
      loadExercise();
    }
  }, [id, getExerciseById, showSnackbar]);

  if (isLoadingExercise) {
    return (
      <ContentContainer>
        <div className={styles.exerciseAttemptPage}>
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner}></div>
            <p>Opdracht laden...</p>
          </div>
        </div>
      </ContentContainer>
    );
  }

  if (!exercise) {
    return (
      <ContentContainer>
        <div className={styles.exerciseAttemptPage}>
          <p>Opdracht niet gevonden</p>
        </div>
      </ContentContainer>
    );
  }

  const totalSteps = exercise.steps.length;
  const isViewingAssets = currentStepIndex === totalSteps;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentStepIndex < totalSteps) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handleComplete = () => {
    showSnackbar('Opdracht voltooid!', 'success');
    navigate('/opdrachten');
  };

  const handleTabClick = (index: number) => {
    setCurrentStepIndex(index);
  };

  return (
    <ContentContainer>
      <div className={styles.exerciseAttemptPage}>
        <div className={styles.headerSection}>
          <Header
            size={4}
            className={styles.header}
            content={
              <div className={styles.headerContent}>
                {exercise.title} <BackButton to="/opdrachten" />
              </div>
            }
          />
        </div>

        <div className={styles.tabsSection}>
          <button key="tabs" className={styles.tab + (!isViewingAssets ? ` ${styles.activeTab}` : '')} onClick={() => handleTabClick(0)}>
            Stappen
          </button>
          <button
            className={`${styles.tab} ${styles.assetsTab} ${isViewingAssets ? styles.activeTab : ''}`}
            onClick={() => handleTabClick(totalSteps)}
          >
            Bestanden
          </button>
        </div>

        {/* Content Section */}
        <div className={styles.contentSection}>
          {isViewingAssets ? (
            <ReadOnlyAssetsTab exerciseId={exercise.id} />
          ) : (
            <StepViewer step={exercise.steps[currentStepIndex]} stepNumber={currentStepIndex + 1} platform={exercise.platform} />
          )}
        </div>

        {/* Navigation Buttons */}
        {!isViewingAssets && (
          <div className={styles.navigationButtons}>
            {!isFirstStep && (
              <Button type="secondary" onClick={handlePrevious}>
                ← Vorige
              </Button>
            )}
            <div className={styles.spacer} />
            {isLastStep ? (
              <Button type="success" onClick={handleComplete}>
                Voltooien ✓
              </Button>
            ) : (
              <Button type="primary" onClick={handleNext}>
                Volgende →
              </Button>
            )}
          </div>
        )}

        {isViewingAssets && (
          <div className={styles.navigationButtons}>
            <Button type="secondary" onClick={() => setCurrentStepIndex(totalSteps - 1)}>
              ← Terug naar laatste stap
            </Button>
          </div>
        )}
      </div>
    </ContentContainer>
  );
};
